import { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, Input, message } from 'antd';
import { SaveOutlined, EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { businessApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';

const DEFAULT_CENTER = [41.2995, 69.2401];
const YANDEX_API_KEY = import.meta.env.VITE_YANDEX_MAPS_API_KEY || '';

function getYmaps(): Record<string, unknown> | null {
  return ((window as unknown as Record<string, unknown>).ymaps as Record<string, unknown>) || null;
}
function call(obj: unknown, method: string, ...args: unknown[]): unknown {
  const o = obj as Record<string, Function>;
  return o[method](...args);
}
function construct(ymaps: Record<string, unknown>, name: string, ...args: unknown[]): Record<string, unknown> {
  const Ctor = ymaps[name] as Function;
  return Reflect.construct(Ctor, args) as Record<string, unknown>;
}

export default function LocationSection() {
  const { t } = useTranslation(['common', 'owner']);
  const { selectedBusiness, updateSelectedBusiness } = useBusinessStore();
  if (!selectedBusiness) return null;

  const [lat, setLat] = useState<number | null>(selectedBusiness.latitude);
  const [lng, setLng] = useState<number | null>(selectedBusiness.longitude);
  const [address, setAddress] = useState(selectedBusiness.address || '');
  const [saving, setSaving] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const ymapsRef = useRef<Record<string, unknown> | null>(null);
  const mapInstanceRef = useRef<Record<string, unknown> | null>(null);
  const markerRef = useRef<Record<string, unknown> | null>(null);

  const hasChanges =
    lat !== selectedBusiness.latitude ||
    lng !== selectedBusiness.longitude ||
    address !== (selectedBusiness.address || '');

  const placeMarker = useCallback((newLat: number, newLng: number) => {
    setLat(newLat); setLng(newLng);
    const ymaps = ymapsRef.current;
    const map = mapInstanceRef.current;
    if (!ymaps || !map) return;
    if (markerRef.current) call(map.geoObjects, 'remove', markerRef.current);
    const marker = construct(ymaps, 'Placemark', [newLat, newLng], {}, { draggable: true, preset: 'islands#redDotIcon' });
    call(marker.events, 'add', 'dragend', () => {
      const coords = call(marker.geometry, 'getCoordinates') as number[];
      setLat(coords[0]); setLng(coords[1]);
    });
    call(map.geoObjects, 'add', marker);
    markerRef.current = marker;
  }, []);

  const initMap = useCallback((ymaps: Record<string, unknown>) => {
    if (!mapRef.current || mapInstanceRef.current) return;
    ymapsRef.current = ymaps;
    const center = lat && lng ? [lat, lng] : DEFAULT_CENTER;
    const map = construct(ymaps, 'Map', mapRef.current, { center, zoom: lat && lng ? 16 : 12, controls: ['zoomControl'] });
    mapInstanceRef.current = map;
    setMapLoaded(true);
    call(map.events, 'add', 'click', (e: unknown) => {
      const coords = call(e, 'get', 'coords') as number[];
      placeMarker(coords[0], coords[1]);
    });
    if (lat && lng) placeMarker(lat, lng);
  }, [lat, lng, placeMarker]);

  useEffect(() => {
    if (!mapRef.current || !YANDEX_API_KEY) return;
    const ymaps = getYmaps();
    if (ymaps) { initMap(ymaps); return; }
    const existing = document.querySelector('script[src*="api-maps.yandex.ru"]');
    if (existing) return;
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${YANDEX_API_KEY}&lang=uz_UZ`;
    script.onload = () => {
      const ym = getYmaps();
      if (!ym) return;
      call(ym, 'ready', () => { const loaded = getYmaps(); if (loaded) initMap(loaded); });
    };
    document.head.appendChild(script);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch: Record<string, unknown> = {};
      if (lat !== null && lng !== null) { patch.latitude = lat; patch.longitude = lng; }
      patch.address = address.trim() || '';
      const updated = await businessApi.updateTenant(selectedBusiness.id, patch);
      updateSelectedBusiness(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('owner:settings.location.saved'));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message ?? t('common:errors.saveFailed'));
      telegram.hapticFeedback.notification('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ borderRadius: 16, background: 'var(--bl-surface, #fff)', border: '1px solid var(--bl-divider, #ebebeb)', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--bl-divider, #ebebeb)' }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--bl-success-soft, #e8f7ee)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--bl-success, #1f8b4c)' }}>
          <EnvironmentOutlined />
        </div>
        <Typography.Text style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl-text-strong, #0f172a)' }}>
          {t('owner:settings.location.title')}
        </Typography.Text>
      </div>

      <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Typography.Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #6b7280)' }}>
          {t('owner:settings.location.hint')}
        </Typography.Text>

        {/* Map */}
        <div ref={mapRef} style={{ width: '100%', height: 200, borderRadius: 12, background: 'var(--bl-surface-soft, #f0f4f8)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {!mapLoaded && !YANDEX_API_KEY && (
            <Typography.Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>{t('owner:settings.location.mapMissingKey')}</Typography.Text>
          )}
          {!mapLoaded && YANDEX_API_KEY && (
            <Typography.Text style={{ fontSize: 12, color: 'var(--bl-text-muted, #9ca3af)' }}>{t('owner:settings.location.mapLoading')}</Typography.Text>
          )}
        </div>

        {/* Coordinates chip */}
        {lat !== null && lng !== null && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--bl-success-soft, #e8f7ee)', borderRadius: 8, padding: '5px 10px', alignSelf: 'flex-start' }}>
            <AimOutlined style={{ fontSize: 12, color: 'var(--bl-success, #1f8b4c)' }} />
            <Typography.Text style={{ fontSize: 12, fontWeight: 600, color: 'var(--bl-success, #1f8b4c)' }}>
              {lat.toFixed(5)}, {lng.toFixed(5)}
            </Typography.Text>
          </div>
        )}

        {/* Address */}
        <div>
          <Typography.Text style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--bl-text-muted, #6b7280)', marginBottom: 6 }}>
            {t('owner:settings.location.addressLabel')}
          </Typography.Text>
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t('owner:settings.location.addressPlaceholder')} maxLength={500} style={{ borderRadius: 12, height: 40 }} />
        </div>

        {hasChanges && (
          <button type="button" onClick={() => void handleSave()} disabled={saving} style={{ width: '100%', height: 46, borderRadius: 12, background: saving ? 'var(--bl-success-soft, #e8f7ee)' : 'var(--bl-success, #1f8b4c)', border: 'none', color: saving ? 'var(--bl-success, #1f8b4c)' : '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: saving ? 0.7 : 1 }}>
            <SaveOutlined /> {saving ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        )}
      </div>
    </div>
  );
}
